import styled from '@emotion/styled';

import type { RisingStarBonusCurrent } from 'services/hooks/risingStarBonus';

import Flex from 'components/util/Flex';

import { useRisingStarCopy } from './copy';
import { formatAmount } from './format';
import {
  formatKstDateTime,
  formatPayoutDate,
  formatSettlementPeriod,
} from '../minimumGuarantee/utils/formatSettlement';
import AmountValue from '../shared/AmountValue';
import { SInformationItemWrapper } from '../shared/style';

/**
 * 산정 월 보너스 요약 (정책 B-1 "지급 예정 보너스" · "산정 기준일과 기간"). 상태·금액은 서버 값 그대로.
 * '다음 산정일'은 `nextCalculationAt` 을 그대로 쓴다 — 산정 뒤 다음 달 기준 시각으로 넘기는 건 서버 몫이다.
 */
export default function BonusSummary({ current, isBlur }: { current: RisingStarBonusCurrent; isBlur: boolean }) {
  const COPY = useRisingStarCopy();
  const { status, schedule } = current;
  const asOfAt = formatKstDateTime(schedule.asOfAt) ?? '-';
  const payoutDate = formatPayoutDate(schedule.payoutDate) ?? '-';

  return (
    <Flex direction="column">
      <Flex align="center" columnGap={8}>
        <SLabel>{COPY.status[status].label(schedule.targetMonth)}</SLabel>
        <SChip $isCancelled={status === 'CANCELLED'}>{COPY.status[status].chip}</SChip>
      </Flex>
      {/* 미지급 금액은 산정만 되고 지급되지 않았다 — 지급액처럼 보이지 않게 취소선으로 남긴다 (BE 가이드, 제외 애셋과 같은 표기) */}
      <SBonus as={status === 'CANCELLED' ? 'del' : 'div'} $isCancelled={status === 'CANCELLED'}>
        {/* 어드민 수정값이 반영된 금액 — calculatedBonus 가 아니라 amount */}
        <AmountValue amountFormat={formatAmount(current.amount)} isBlur={isBlur} />
      </SBonus>
      <SSub>{COPY.statusSub[status](asOfAt, payoutDate)}</SSub>
      {/* 미지급은 지급일에 샛별이 아니게 된 분이라 다음 산정 대상이 아니다 */}
      {status !== 'CANCELLED' && (
        <SInformationItemWrapper>
          <span>{COPY.nextCalculateAt}</span>
          <span className="bold">{formatKstDateTime(schedule.nextCalculationAt) ?? '-'}</span>
        </SInformationItemWrapper>
      )}
      <SInformationItemWrapper>
        <span>{COPY.payoutDate}</span>
        <span className="bold">{payoutDate}</span>
      </SInformationItemWrapper>
      <SInformationItemWrapper $invisibleBorderBottom>
        <span>{COPY.period}</span>
        <span className="bold">{formatSettlementPeriod(schedule.periodStartAt, schedule.periodEndAt) ?? '-'}</span>
      </SInformationItemWrapper>
    </Flex>
  );
}

const SLabel = styled.span`
  color: ${({ theme }) => theme.color.label.secondary};
  ${({ theme }) => theme.font.body2_m};
`;

// 미지급 칩은 브랜드색(지급 완료와 같은 톤) 대신 흐린 회색 — 성공 상태처럼 보이지 않게
const SChip = styled.span<{ $isCancelled: boolean }>`
  padding: 2px 8px;
  border-radius: 999px;
  background-color: ${({ theme, $isCancelled }) =>
    $isCancelled ? theme.color.bg.secondary : theme.color.highlight.primary['10']};
  color: ${({ theme, $isCancelled }) => ($isCancelled ? theme.color.label.tertiary : theme.color.brand.primary)};
  ${({ theme }) => theme.font.caption1_sb};
`;

const SBonus = styled.div<{ $isCancelled: boolean }>`
  display: block;
  margin-top: 4px;
  color: ${({ theme, $isCancelled }) => ($isCancelled ? theme.color.label.tertiary : theme.color.label.primary)};
  ${({ theme }) => theme.font.head4_b};
`;

const SSub = styled.p`
  margin-top: 4px;
  color: ${({ theme }) => theme.color.label.tertiary};
  ${({ theme }) => theme.font.caption1_m};
`;
